// Canvas-side block renderers. Mirror the structure of the runtime
// renderers in `renderers.tsx` but enable inline editing on every text
// field and per-list add/remove for blocks that maintain a list.
//
// The runtime renderers stay read-only for the actual page output; these
// editable variants are used exclusively by BlockNode in the Page Designer.

import { useState } from 'react'
import { ChevronDown, Check, Star, X } from 'lucide-react'
import type {
  AccordionBlock, ButtonGroupBlock, ButtonBlock, ButtonSpec,
  CalloutBlock, CardBlock, CardGridBlock, CardSpec, ColumnsBlock,
  ContactFormBlock, ContainerBlock, CtaBannerBlock, FaqBlock,
  FeatureGridBlock, GalleryBlock, HeroBlock, LogoCloudBlock,
  NewsletterBlock, PricingBlock, StatsBlock, CountdownBlock, TabsBlock,
  TestimonialBlock, TimelineBlock, Block, TableBlock,
  EntityPickerBlock, NewCustomerFormBlock, ProductSelectionBlock,
  ProductPreviewBlock, OrderSummaryBlock, OrderSubmitBlock, PaymentCheckoutBlock,
  PaymentSettingsBlock,
  FileUploadBlock, FilterValueRef,
} from './types'
import { outputVarName } from '@/livepreview/bindingResolver'
import { ECOM_ENDPOINTS, resolveEndpointUrl } from '@/livepreview/ecommerce/endpoints'
import { newId, makeFeatureItem, makeGalleryItem, makeLogoItem, makeCard, makePricingTier, makeTimelineItem } from './blocks'
import { PageIcon } from '@twasta/page-runtime/iconMap'
import { toArrayRow } from './TableBlockEditor'
import { reindexListBindings, removeOrder } from './listBindings'
import { backgroundStyle, PageButton, buttonSpecOf, CountdownRenderer, HeroCarousel, hasHeroSlides, heroRadius, heroTitleSize, containerLayout } from './renderers'
import { bgHasFill, SHADOWS, themeRem } from './theme'
import { resolveIcon } from './iconMap'
import EditableText from './EditableText'
import { InlineListItem, AddItemButton } from './InlineListControls'
import ChildBlockList from './ChildBlockList'
import { ProductPreviewPlacementEditor } from './ProductPreviewPlacementEditor'
import { useViewport } from './ViewportContext'

// Lazy-promote a legacy plain-text `body` (or `text`) into a single
// paragraph block when the slot first gets a child block. Prevents
// authors from "losing" content the moment they switch a tab/accordion
// item from text-only to nested-block mode.
function promoteLegacyText(legacy: string | undefined, next: Block[]): Block[] {
  if (!legacy || !legacy.trim()) return next
  const legacyBlock: Block = { id: newId('paragraph'), type: 'paragraph', text: legacy } as Block
  return [legacyBlock, ...next]
}

type Patcher<B extends Block> = (next: B) => void

// Responsive grid template — targets `cols` columns when the
// container is wide enough, but each column is enforced to be at
// least `minPx` wide. When the viewport (or canvas viewport in the
// designer's mobile preview) narrows below `cols * minPx`, the grid
// wraps automatically. `auto-fit` collapses empty tracks so a single
// item fills the row instead of leaving phantom columns.
//
// Reused by every grid-style block (feature_grid, stats, pricing,
// gallery, columns) so they all degrade consistently on mobile.
export function responsiveGrid(cols: number, minPx: number, gap = 16): string {
  const safeCols = Math.max(1, cols || 1)
  return `repeat(auto-fit, minmax(max(${minPx}px, calc((100% - ${(safeCols - 1) * gap}px) / ${safeCols})), 1fr))`
}

// Mirror of page-runtime's columnTrack: a per-column width ("180px"/"30%"/
// number→px) → CSS track, or null = fill the remaining space (1fr).
export function columnTrack(width: string | number | undefined): string | null {
  if (width === undefined || width === null) return null
  if (typeof width === 'number') return Number.isFinite(width) ? `${width}px` : null
  const w = width.trim()
  if (!w) return null
  if (/^\d+(\.\d+)?$/.test(w)) return `${w}px`
  return w
}

// ─── Hero ──────────────────────────────────────────────

export function HeroCanvas({ block, onChange }: { block: HeroBlock; onChange: Patcher<HeroBlock> }) {
  const align = block.align
  const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'
  const slides = hasHeroSlides(block)
  // Default to the page palette text color when the hero has no explicit
  // textColor — keeps the title legible on light themes (e.g. Bold's cream
  // gradient). Over a carousel default to white (busy/dark imagery). The fresh
  // hero ships with textColor for the dark-gradient default; only blocks where
  // textColor was cleared fall back here.
  const text: React.CSSProperties = {
    color: block.textColor || (slides ? '#ffffff' : (bgHasFill(block.background) ? 'var(--pg-inverted, #fff)' : 'var(--pg-text, #1e293b)')),
  }
  const hasSideImage = !slides && block.imageSide && block.imageSide !== 'none' && block.imageUrl
  const HEIGHT: Record<NonNullable<HeroBlock['height']>, string> = {
    compact: '320px', normal: '460px', tall: '600px', screen: '80vh',
  }

  // Editable eyebrow / title / subtitle / CTAs — shared by both layouts.
  const content = (
    <>
      <EditableText
        value={block.eyebrow || ''}
        onChange={(eyebrow) => onChange({ ...block, eyebrow })}
        placeholder="Eyebrow"
        multiline={false}
        style={{ ...text, fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.85, padding: '4px 10px', background: 'rgba(255,255,255,0.12)', borderRadius: 999 }}
      />
      <EditableText
        value={block.title || ''}
        onChange={(title) => onChange({ ...block, title })}
        placeholder="Hero title"
        multiline={false}
        style={{ ...text, ...heroTitleSize(block), lineHeight: 1.1, margin: 0, maxWidth: 760 }}
      />
      <EditableText
        value={block.subtitle || ''}
        onChange={(subtitle) => onChange({ ...block, subtitle })}
        placeholder="Subtitle"
        style={{ ...text, fontSize: '1.1125rem', lineHeight: 1.5, margin: 0, opacity: 0.92, maxWidth: 620 }}
      />
      {(block.primaryCta || block.secondaryCta) && (
        <div style={{ display: 'flex', gap: 10, marginTop: 8, ...text, flexWrap: 'wrap', justifyContent: justify }}>
          {block.primaryCta && <PageButton btn={block.primaryCta} />}
          {block.secondaryCta && <PageButton btn={block.secondaryCta} />}
        </div>
      )}
    </>
  )

  if (slides) {
    return (
      <HeroCarousel block={block} minHeight={HEIGHT[block.height || 'normal']} disableAutoplay>
        {content}
      </HeroCarousel>
    )
  }

  return (
    <section
      style={{
        ...backgroundStyle(block.background),
        minHeight: HEIGHT[block.height || 'normal'],
        borderRadius: heroRadius(block), overflow: 'hidden', padding: '48px 36px',
        display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap',
        flexDirection: hasSideImage && block.imageSide === 'right' ? 'row' : 'row-reverse',
      }}
    >
      {hasSideImage && (
        <div style={{ flex: '1 1 280px', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
          <img src={block.imageUrl} alt="" style={{ maxWidth: '100%', maxHeight: 380, borderRadius: 12, boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }} />
        </div>
      )}
      <div style={{ flex: '1 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16, alignItems: justify, textAlign: align }}>
        {content}
      </div>
    </section>
  )
}

// ─── CTA Banner ────────────────────────────────────────

export function CtaBannerCanvas({ block, onChange }: { block: CtaBannerBlock; onChange: Patcher<CtaBannerBlock> }) {
  const align = block.align
  const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'
  const text: React.CSSProperties = { color: block.textColor || (bgHasFill(block.background) ? 'var(--pg-inverted, #fff)' : 'var(--pg-text, #1e293b)') }
  return (
    <section
      style={{
        ...backgroundStyle(block.background),
        borderRadius: 12, padding: '32px 28px',
        display: 'flex', flexDirection: 'column', gap: 12,
        alignItems: justify, textAlign: align,
      }}
    >
      <EditableText
        value={block.eyebrow || ''}
        onChange={(eyebrow) => onChange({ ...block, eyebrow })}
        placeholder="Eyebrow"
        multiline={false}
        style={{ ...text, fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.9 }}
      />
      <EditableText
        value={block.title || ''}
        onChange={(title) => onChange({ ...block, title })}
        placeholder="CTA title"
        multiline={false}
        style={{ ...text, fontSize: '1.8625rem', fontWeight: 800, margin: 0, lineHeight: 1.2 }}
      />
      <EditableText
        value={block.subtitle || ''}
        onChange={(subtitle) => onChange({ ...block, subtitle })}
        placeholder="Subtitle"
        style={{ ...text, fontSize: '1.0625rem', margin: 0, opacity: 0.9, maxWidth: 600 }}
      />
      {(block.primaryCta || block.secondaryCta) && (
        <div style={{ display: 'flex', gap: 10, marginTop: 6, ...text, flexWrap: 'wrap', justifyContent: justify }}>
          {block.primaryCta && <PageButton btn={block.primaryCta} />}
          {block.secondaryCta && <PageButton btn={block.secondaryCta} />}
        </div>
      )}
    </section>
  )
}

// ─── Container ─────────────────────────────────────────

export function ContainerCanvas({ block, onChange }: { block: ContainerBlock; onChange: Patcher<ContainerBlock> }) {
  const hasChildren = !!block.children && block.children.length > 0
  const box = containerLayout(block)
  return (
    <section
      style={{
        ...backgroundStyle(block.background),
        padding: block.padding ?? 24,
        borderRadius: block.rounded ? 12 : 0,
        textAlign: block.align || 'left',
        color: block.textColor || 'inherit',
        whiteSpace: 'pre-wrap',
        lineHeight: 1.55,
        ...box.outer,
      }}
    >
      {hasChildren ? (
        <div style={box.inner}>
          <ChildBlockList
            blocks={block.children!}
            onChange={(next) => onChange({ ...block, children: next })}
          />
        </div>
      ) : (
        <>
          <EditableText
            value={block.text || ''}
            onChange={(text) => onChange({ ...block, text })}
            placeholder="Container content (plain text) — or insert a block below"
          />
          <div style={{ marginTop: 10 }}>
            <ChildBlockList
              blocks={[]}
              onChange={(next) => onChange({
                ...block,
                children: promoteLegacyText(block.text, next),
                text: '',
              })}
            />
          </div>
        </>
      )}
    </section>
  )
}

// ─── Columns ───────────────────────────────────────────

export function ColumnsCanvas({ block, onChange }: { block: ColumnsBlock; onChange: Patcher<ColumnsBlock> }) {
  // The canvas simulates responsiveness via the device toggle (viewport),
  // not real @media — so derive the stacking decision from the previewed
  // device instead. Tablet (768) and mobile (390) are both at/below the
  // runtime's tablet breakpoint (1024), where fixed-width columns stack.
  const viewport = useViewport()
  const items = block.columns.slice(0, block.count)
  while (items.length < block.count) items.push({ id: newId('col'), text: '', blocks: [] })
  const updateCol = (idx: number, patch: Partial<typeof block.columns[number]>) => {
    const cols = [...block.columns]
    cols[idx] = { ...(cols[idx] ?? { id: newId('col') }), ...patch }
    onChange({ ...block, columns: cols })
  }
  // Per-column sizing (mirrors page-runtime ColumnsRenderer): explicit
  // widths opt out of the responsive equal-split; unsized columns → 1fr.
  const tracks = items.map((c) => columnTrack(c.width))
  const hasExplicit = tracks.some((t) => t !== null)
  const hasFill = tracks.some((t) => t === null)
  // Fixed-width tracks don't reflow, so stack them into one column when
  // previewing a non-desktop device — matching the runtime @media stack.
  const stacked = hasExplicit && viewport !== 'desktop'
  const justifyMap = { start: 'start', center: 'center', end: 'end', between: 'space-between' } as const
  return (
    <div
      style={{
        display: 'grid',
        // Each column should be at least 200px to remain readable;
        // narrower than that and we wrap to fewer columns. Text-heavy
        // column content stops collapsing into a one-word stripe.
        gridTemplateColumns: stacked
          ? '1fr'
          : hasExplicit
            ? tracks.map((t) => t ?? '1fr').join(' ')
            : responsiveGrid(block.count, 200, block.gap ?? 24),
        gap: block.gap ?? 24,
        alignItems: block.align === 'center' ? 'center' : block.align === 'bottom' ? 'end' : 'start',
        ...(!stacked && hasExplicit && !hasFill ? { justifyContent: justifyMap[block.justify || 'start'] } : null),
      }}
    >
      {items.map((col, i) => {
        const hasChildren = !!col.blocks && col.blocks.length > 0
        return (
          <div
            key={col.id}
            style={{
              padding: 12, borderRadius: 8,
              background: 'var(--pg-bg)', border: '1px dashed var(--pg-border)',
              color: 'var(--pg-text)', fontSize: '0.9625rem', lineHeight: 1.5, minHeight: 60,
            }}
          >
            {hasChildren ? (
              <ChildBlockList
                blocks={col.blocks!}
                onChange={(next) => updateCol(i, { blocks: next })}
              />
            ) : (
              <>
                <EditableText
                  value={col.text || ''}
                  onChange={(t) => updateCol(i, { text: t })}
                  placeholder={`Column ${i + 1} content…`}
                />
                <div style={{ marginTop: 8 }}>
                  <ChildBlockList
                    blocks={[]}
                    onChange={(next) => updateCol(i, {
                      blocks: promoteLegacyText(col.text, next),
                      text: '',
                    })}
                  />
                </div>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Card / Card Grid ──────────────────────────────────

function CardEditable({ card, look, onChange }: {
  card: CardSpec; look?: CardGridBlock['cardStyle']; onChange: (c: CardSpec) => void
}) {
  // Same frame + fit + hover swap the runtime draws (renderers.tsx CardMedia),
  // so the canvas shows a product shot whole when the author asks for it.
  const fit = card.imageFit || look?.imageFit || 'cover'
  const aspect = card.imageAspect || look?.imageAspect
  const layer: React.CSSProperties = {
    position: 'absolute', inset: 0, width: '100%', height: '100%',
    objectFit: fit, display: 'block', transition: 'opacity 0.35s ease',
  }
  // The card's own look, as the runtime's CardView draws it: the outline
  // goes only with `border: false` (a card background no longer hides it).
  return (
    <div className="pg-card" style={{
      display: 'flex', flexDirection: 'column',
      background: card.background || 'var(--pg-surface)',
      border: look?.border === false ? 'none' : '1px solid var(--pg-border)',
      borderRadius: look?.radius ?? 12, overflow: 'hidden', height: '100%',
      boxShadow: look?.shadow ? SHADOWS[look.shadow] : undefined,
      position: card.wishlist ? 'relative' : undefined,
    }}>
      {card.imageUrl && (
        <div
          className={card.hoverImageUrl ? 'pg-card-media pg-card-media--swap' : 'pg-card-media'}
          style={{ position: 'relative', width: '100%', overflow: 'hidden', ...(aspect ? { aspectRatio: aspect } : { height: look?.imageHeight ?? 160 }) }}
        >
          <img className="pg-card-img" src={card.imageUrl} alt={card.imageAlt ?? card.title ?? ''} style={layer} />
          {card.hoverImageUrl && (
            <>
              <img className="pg-card-img-alt" src={card.hoverImageUrl} alt="" aria-hidden="true" style={{ ...layer, opacity: 0 }} />
              <style>{'.pg-card:hover .pg-card-media--swap .pg-card-img{opacity:0!important}.pg-card:hover .pg-card-media--swap .pg-card-img-alt{opacity:1!important}'}</style>
            </>
          )}
        </div>
      )}
      {card.wishlist && (
        <span aria-hidden style={{
          position: 'absolute', top: 10, right: 10, zIndex: 2, width: 36, height: 36, borderRadius: 999,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid var(--pg-border)', background: 'var(--pg-surface)', color: 'var(--pg-text)',
        }}>♡</span>
      )}
      <div style={{ padding: look?.padding ?? 16, display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
        {card.badge !== undefined && (
          <EditableText
            value={card.badge}
            onChange={(badge) => onChange({ ...card, badge })}
            placeholder="Badge"
            multiline={false}
            style={{
              alignSelf: 'flex-start', fontSize: themeRem(0.65), fontWeight: 700,
              padding: '2px 8px', borderRadius: 999,
              background: 'var(--pg-info-bg, var(--pg-surface-alt))', color: 'var(--pg-accent)',
              textTransform: 'uppercase', letterSpacing: '0.06em',
            }}
          />
        )}
        {card.eyebrow !== undefined && (
          <EditableText
            value={card.eyebrow}
            onChange={(eyebrow) => onChange({ ...card, eyebrow })}
            placeholder="Eyebrow"
            multiline={false}
            style={{
              fontSize: themeRem(0.72), fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: 'var(--pg-accent)',
            }}
          />
        )}
        <EditableText
          value={card.title || ''}
          onChange={(title) => onChange({ ...card, title })}
          placeholder="Card title"
          multiline={false}
          style={{ margin: 0, fontSize: look?.titleSize ? `${look.titleSize}px` : themeRem(1.05), fontWeight: 700, color: 'var(--pg-text)' }}
        />
        <EditableText
          value={card.subtitle || ''}
          onChange={(subtitle) => onChange({ ...card, subtitle })}
          placeholder="Subtitle"
          multiline={false}
          style={{ fontSize: themeRem(0.78), color: 'var(--pg-text-secondary)', fontWeight: 500 }}
        />
        <EditableText
          value={card.body || ''}
          onChange={(body) => onChange({ ...card, body })}
          placeholder="Card description"
          style={{ margin: '4px 0 0', fontSize: look?.bodySize ? `${look.bodySize}px` : themeRem(0.88), color: 'var(--pg-text-secondary)', lineHeight: 1.5 }}
        />
        {(card.price !== undefined || card.status) && (
          <div style={{
            marginTop: 'auto', paddingTop: 10,
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            gap: 10, flexWrap: 'wrap',
          }}>
            {card.price !== undefined && (
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                <EditableText
                  value={card.price}
                  onChange={(price) => onChange({ ...card, price })}
                  placeholder="Price"
                  multiline={false}
                  style={{
                    fontSize: themeRem(1.3), fontWeight: 800, letterSpacing: '-0.02em',
                    color: 'var(--pg-text)',
                  }}
                />
                {card.compareAtPrice ? (
                  <s style={{ fontSize: themeRem(0.85), color: 'var(--pg-text-secondary)' }}>{card.compareAtPrice}</s>
                ) : null}
                {card.priceNote !== undefined && (
                  <EditableText
                    value={card.priceNote}
                    onChange={(priceNote) => onChange({ ...card, priceNote })}
                    placeholder="per unit"
                    multiline={false}
                    style={{ fontSize: themeRem(0.74), color: 'var(--pg-text-secondary)' }}
                  />
                )}
              </span>
            )}
            {card.status && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: themeRem(0.74),
                fontWeight: 600, color: 'var(--pg-success, #16a34a)',
                background: 'var(--pg-success-bg, rgba(22,163,74,.10))',
                padding: '3px 9px', borderRadius: 999,
              }}>
                <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor' }} />
                <EditableText
                  value={card.status.label}
                  onChange={(label) => onChange({ ...card, status: { ...card.status!, label } })}
                  placeholder="In stock"
                  multiline={false}
                  style={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
                />
              </span>
            )}
          </div>
        )}
        {(card.cta || card.secondaryCta) && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {card.cta && <PageButton btn={card.cta} />}
            {card.secondaryCta && <PageButton btn={card.secondaryCta} />}
          </div>
        )}
      </div>
    </div>
  )
}

export function CardCanvas({ block, onChange }: { block: CardBlock; onChange: Patcher<CardBlock> }) {
  // Repeated in a grid (or `fill`), the card fills its cell like the
  // runtime's CardRenderer; standalone it keeps the 360px cap.
  const inGrid = !!block.repeat?.source && block.repeat.layout?.mode === 'grid'
  const fill = inGrid || !!block.fill
  return (
    <div style={{ maxWidth: fill ? undefined : 360, flex: inGrid ? '1 1 auto' : undefined, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ flex: inGrid ? '1 1 auto' : undefined, display: 'flex', flexDirection: 'column' }}>
        <CardEditable card={block.card} look={block.cardStyle} onChange={(c) => onChange({ ...block, card: c })} />
      </div>
      <div style={{
        padding: 8, border: '1px dashed var(--pg-border)', borderRadius: 8,
        background: 'var(--pg-bg)',
      }}>
        <div style={{
          fontSize: '0.8125rem', color: 'var(--pg-text-secondary)',
          marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          Card body slot
        </div>
        <ChildBlockList
          blocks={block.children ?? []}
          onChange={(next) => onChange({ ...block, children: next })}
          emptyLabel="No extra blocks. The card spec above renders by default."
        />
      </div>
    </div>
  )
}

export function CardGridCanvas({ block, onChange }: { block: CardGridBlock; onChange: Patcher<CardGridBlock> }) {
  const updateCard = (idx: number, c: CardSpec) => {
    const next = [...block.cards]; next[idx] = c
    onChange({ ...block, cards: next })
  }
  const removeCard = (idx: number) => onChange({ ...block, cards: block.cards.filter((_, k) => k !== idx), bindings: reindexListBindings(block, 'cards', removeOrder(block.cards.length, idx)) })
  const addCard = () => onChange({ ...block, cards: [...block.cards, makeCard()] })
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: responsiveGrid(block.columns, 240, block.gap ?? 16),
        gap: block.gap ?? 16,
      }}>
        {block.cards.map((c, i) => (
          <InlineListItem
            key={c.id}
            onRemove={block.cards.length > 1 ? () => removeCard(i) : undefined}
            removeLabel="Remove card"
            style={{ height: '100%' }}
          >
            <CardEditable card={c} look={block.cardStyle} onChange={(nc) => updateCard(i, nc)} />
          </InlineListItem>
        ))}
      </div>
      <div><AddItemButton label="Add card" onClick={addCard} /></div>
    </div>
  )
}

// ─── Feature Grid ──────────────────────────────────────

export function FeatureGridCanvas({ block, onChange }: { block: FeatureGridBlock; onChange: Patcher<FeatureGridBlock> }) {
  const align = block.align || 'left'
  const updateItem = (idx: number, patch: Partial<typeof block.items[number]>) => {
    const items = [...block.items]; items[idx] = { ...items[idx], ...patch }
    onChange({ ...block, items })
  }
  const removeItem = (idx: number) => onChange({ ...block, items: block.items.filter((_, k) => k !== idx) })
  const addItem = () => onChange({ ...block, items: [...block.items, makeFeatureItem()] })
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <header style={{ textAlign: align, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <EditableText
          value={block.eyebrow || ''}
          onChange={(eyebrow) => onChange({ ...block, eyebrow })}
          placeholder="Eyebrow"
          multiline={false}
          style={{ fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--pg-accent)' }}
        />
        <EditableText
          value={block.heading || ''}
          onChange={(heading) => onChange({ ...block, heading })}
          placeholder="Section heading"
          multiline={false}
          style={{ margin: 0, fontSize: '1.7625rem', fontWeight: 800, color: 'var(--pg-text)' }}
        />
        <EditableText
          value={block.subheading || ''}
          onChange={(subheading) => onChange({ ...block, subheading })}
          placeholder="Section subheading"
          style={{ margin: 0, fontSize: '1.0125rem', color: 'var(--pg-text-secondary)', maxWidth: 640, alignSelf: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start' }}
        />
      </header>
      {/* Responsive — collapses below `block.columns` columns when each
          would shrink below 220px. See responsiveGrid() comment. */}
      <div style={{
        display: 'grid', gridTemplateColumns: responsiveGrid(block.columns, 220, 20), gap: 20,
      }}>
        {block.items.map((it, i) => (
          <InlineListItem
            key={it.id}
            onRemove={block.items.length > 1 ? () => removeItem(i) : undefined}
            removeLabel="Remove feature"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, textAlign: align }}>
              <div style={{
                width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.4625rem',
                background: block.iconStyle === 'plain' ? 'transparent' : 'var(--pg-info-bg, var(--pg-surface-alt))',
                color: 'var(--pg-accent)',
                borderRadius: block.iconStyle === 'square' ? 8 : block.iconStyle === 'circle' ? 999 : 0,
                alignSelf: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
              }}>
                {resolveIcon(it.icon)}
              </div>
              <EditableText
                value={it.title || ''}
                onChange={(title) => updateItem(i, { title })}
                placeholder="Feature title"
                multiline={false}
                style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 700, color: 'var(--pg-text)' }}
              />
              <EditableText
                value={it.description || ''}
                onChange={(description) => updateItem(i, { description })}
                placeholder="Feature description"
                style={{ margin: 0, fontSize: '0.9125rem', color: 'var(--pg-text-secondary)', lineHeight: 1.5 }}
              />
            </div>
          </InlineListItem>
        ))}
      </div>
      <div><AddItemButton label="Add feature" onClick={addItem} /></div>
    </section>
  )
}

// ─── Stats ─────────────────────────────────────────────

export function StatsCanvas({ block, onChange }: { block: StatsBlock; onChange: Patcher<StatsBlock> }) {
  const updateItem = (idx: number, patch: Partial<typeof block.items[number]>) => {
    const items = [...block.items]; items[idx] = { ...items[idx], ...patch }
    onChange({ ...block, items })
  }
  const removeItem = (idx: number) => onChange({ ...block, items: block.items.filter((_, k) => k !== idx) })
  const addItem = () => onChange({
    ...block, items: [...block.items, { id: newId('stat'), value: '0', label: 'Label' }],
  })
  return (
    <section style={{
      ...backgroundStyle(block.background),
      borderRadius: bgHasFill(block.background) ? 12 : 0,
      padding: bgHasFill(block.background) ? '28px 24px' : 0,
      display: 'flex', flexDirection: 'column', gap: 16,
    }}>
      <EditableText
        value={block.heading || ''}
        onChange={(heading) => onChange({ ...block, heading })}
        placeholder="Stats heading"
        multiline={false}
        style={{ margin: 0, textAlign: block.align || 'center', fontSize: '1.4625rem', fontWeight: 700, color: 'var(--pg-text)' }}
      />
      <div style={{
        display: 'grid',
        gridTemplateColumns: responsiveGrid(block.columns, 140, 16),
        gap: 16,
      }}>
        {block.items.map((it, i) => (
          <InlineListItem
            key={it.id}
            onRemove={block.items.length > 1 ? () => removeItem(i) : undefined}
            removeLabel="Remove stat"
          >
            <div style={{ textAlign: block.align || 'center', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: block.align === 'center' ? 'center' : block.align === 'right' ? 'flex-end' : 'flex-start', alignItems: 'baseline', gap: 2 }}>
                <EditableText
                  value={it.value || ''}
                  onChange={(value) => updateItem(i, { value })}
                  placeholder="0"
                  multiline={false}
                  style={{ fontSize: '2.2625rem', fontWeight: 800, color: 'var(--pg-primary)', lineHeight: 1.1 }}
                />
                <EditableText
                  value={it.suffix || ''}
                  onChange={(suffix) => updateItem(i, { suffix })}
                  placeholder=""
                  multiline={false}
                  style={{ fontSize: '1.4625rem', opacity: 0.85, color: 'var(--pg-primary)' }}
                />
              </div>
              <EditableText
                value={it.label || ''}
                onChange={(label) => updateItem(i, { label })}
                placeholder="Label"
                multiline={false}
                style={{ fontSize: '0.9125rem', fontWeight: 600, color: 'var(--pg-text)' }}
              />
              <EditableText
                value={it.description || ''}
                onChange={(description) => updateItem(i, { description })}
                placeholder="Description"
                multiline={false}
                style={{ fontSize: '0.8125rem', color: 'var(--pg-text-secondary)' }}
              />
            </div>
          </InlineListItem>
        ))}
      </div>
      <div><AddItemButton label="Add stat" onClick={addItem} /></div>
    </section>
  )
}

// ─── Countdown ──────────────────────────────────────────
// The countdown's content (target date, labels, heading) is edited in the
// property panel, so the canvas just shows a live ticking preview via the
// shared runtime renderer — what the author sees is what ships.
export function CountdownCanvas({ block }: { block: CountdownBlock; onChange: Patcher<CountdownBlock> }) {
  return <CountdownRenderer block={block} />
}

// ─── Timeline ──────────────────────────────────────────

export function TimelineCanvas({ block, onChange }: { block: TimelineBlock; onChange: Patcher<TimelineBlock> }) {
  const headerAlign = block.align || 'left'
  const layout = block.layout || 'vertical'
  const markerStyle = block.markerStyle || 'dot'
  const accent = block.accentColor || 'var(--pg-primary)'
  const updateItem = (idx: number, patch: Partial<typeof block.items[number]>) => {
    const items = [...block.items]; items[idx] = { ...items[idx], ...patch }
    onChange({ ...block, items })
  }
  const removeItem = (idx: number) => onChange({ ...block, items: block.items.filter((_, k) => k !== idx) })
  const addItem = () => onChange({ ...block, items: [...block.items, makeTimelineItem('', 'New milestone', '', '✨')] })
  const isAlt = layout === 'alternating'

  // Each row is its own grid: marker column + card column. The connecting
  // line is painted as a pseudo-element on the parent <ol> (handled by
  // inline-CSS in the runtime; here we inline an absolute-positioned line
  // so the canvas matches the runtime visual.)
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <header style={{ textAlign: headerAlign, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <EditableText
          value={block.eyebrow || ''}
          onChange={(eyebrow) => onChange({ ...block, eyebrow })}
          placeholder="Eyebrow"
          multiline={false}
          style={{ fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: accent }}
        />
        <EditableText
          value={block.heading || ''}
          onChange={(heading) => onChange({ ...block, heading })}
          placeholder="Section heading"
          multiline={false}
          style={{ margin: 0, fontSize: '1.7625rem', fontWeight: 800, color: 'var(--pg-text)' }}
        />
        <EditableText
          value={block.subheading || ''}
          onChange={(subheading) => onChange({ ...block, subheading })}
          placeholder="Section subheading"
          style={{ margin: 0, fontSize: '1.0125rem', color: 'var(--pg-text-secondary)', maxWidth: 640, alignSelf: headerAlign === 'center' ? 'center' : headerAlign === 'right' ? 'flex-end' : 'flex-start' }}
        />
      </header>

      <ol style={{ position: 'relative', listStyle: 'none', margin: 0, padding: 0 }}>
        {/* Connecting axis */}
        <div style={{
          position: 'absolute', top: 6, bottom: 6,
          width: 2,
          left: isAlt ? 'calc(50% - 1px)' : 17,
          background: `linear-gradient(180deg,
            color-mix(in srgb, ${accent} 70%, transparent) 0%,
            color-mix(in srgb, ${accent} 25%, transparent) 100%)`,
          borderRadius: 2,
          pointerEvents: 'none',
        }} />
        {block.items.map((it, i) => {
          const itemAccent = it.accent || accent
          const side: 'left' | 'right' = isAlt ? (i % 2 === 0 ? 'left' : 'right') : 'right'
          const cols = isAlt ? '1fr 36px 1fr' : '36px 1fr'
          const cardCol = isAlt ? (side === 'left' ? 1 : 3) : 2
          const markerCol = isAlt ? 2 : 1
          const cardAlign: React.CSSProperties = isAlt && side === 'left' ? { textAlign: 'right' } : {}
          const markerContent =
            markerStyle === 'icon' ? (it.icon || '•') :
            markerStyle === 'number' ? String(i + 1) :
            ''
          return (
            <li key={it.id} style={{ position: 'relative', padding: '4px 0 24px' }}>
              <InlineListItem
                onRemove={block.items.length > 1 ? () => removeItem(i) : undefined}
                removeLabel="Remove milestone"
                style={{
                  display: 'grid',
                  gridTemplateColumns: cols,
                  alignItems: 'start',
                  gap: 12,
                  position: 'relative',
                }}
              >
                <>
                  <div style={{
                    gridColumn: markerCol,
                    width: 36, height: 36, borderRadius: 999,
                    background: itemAccent,
                    color: 'var(--pg-on-primary, #fff)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.1125rem', fontWeight: 700,
                    boxShadow: `0 0 0 4px var(--pg-surface, #fff), 0 4px 12px color-mix(in srgb, ${itemAccent} 30%, transparent)`,
                    position: 'relative', zIndex: 1,
                  }}>
                    {markerStyle !== 'dot' && <span style={{ lineHeight: 1 }}>{markerContent}</span>}
                  </div>
                  <div style={{
                    gridColumn: cardCol,
                    display: 'flex', flexDirection: 'column', gap: 6,
                    padding: '12px 14px',
                    background: 'var(--pg-surface)',
                    border: '1px solid var(--pg-border)',
                    borderRadius: 10,
                    boxShadow: '0 1px 2px color-mix(in srgb, var(--pg-text) 6%, transparent)',
                    ...cardAlign,
                  }}>
                    <EditableText
                      value={it.date || ''}
                      onChange={(date) => updateItem(i, { date })}
                      placeholder="Date / label"
                      multiline={false}
                      style={{
                        fontSize: '0.8125rem', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.08em',
                        color: itemAccent,
                      }}
                    />
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                      justifyContent: cardAlign.textAlign === 'right' ? 'flex-end' : 'flex-start',
                    }}>
                      <EditableText
                        value={it.title || ''}
                        onChange={(title) => updateItem(i, { title })}
                        placeholder="Milestone title"
                        multiline={false}
                        style={{ margin: 0, fontSize: '1.1125rem', fontWeight: 700, color: 'var(--pg-text)' }}
                      />
                      {(it.badge || true) && (
                        <EditableText
                          value={it.badge || ''}
                          onChange={(badge) => updateItem(i, { badge })}
                          placeholder="Badge"
                          multiline={false}
                          style={{
                            fontSize: '0.8125rem', fontWeight: 700,
                            padding: '2px 8px', borderRadius: 999,
                            background: `color-mix(in srgb, ${itemAccent} 14%, transparent)`,
                            color: itemAccent,
                            textTransform: 'uppercase', letterSpacing: '0.05em',
                            minWidth: 28,
                          }}
                        />
                      )}
                    </div>
                    <EditableText
                      value={it.description || ''}
                      onChange={(description) => updateItem(i, { description })}
                      placeholder="Short description"
                      style={{ margin: 0, fontSize: '0.9625rem', color: 'var(--pg-text-secondary)', lineHeight: 1.55 }}
                    />
                  </div>
                </>
              </InlineListItem>
            </li>
          )
        })}
      </ol>
      <div><AddItemButton label="Add milestone" onClick={addItem} /></div>
    </section>
  )
}

// ─── Testimonial ───────────────────────────────────────

export function TestimonialCanvas({ block, onChange }: { block: TestimonialBlock; onChange: Patcher<TestimonialBlock> }) {
  const align = block.align || 'center'
  const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'
  return (
    <figure style={{
      margin: 0, padding: 28,
      background: 'var(--pg-surface)', border: '1px solid var(--pg-border)', borderRadius: 12,
      display: 'flex', flexDirection: 'column', gap: 14, alignItems: justify, textAlign: align,
    }}>
      {block.rating !== undefined && block.rating > 0 && (
        <div style={{ display: 'flex', gap: 2, color: '#fbbf24' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} size={16} fill={i < (block.rating || 0) ? '#fbbf24' : 'transparent'} />
          ))}
        </div>
      )}
      <EditableText
        value={block.quote || ''}
        onChange={(quote) => onChange({ ...block, quote })}
        placeholder="“Customer quote here.”"
        style={{ margin: 0, fontSize: '1.1125rem', lineHeight: 1.6, color: 'var(--pg-text)', fontStyle: 'italic', maxWidth: 720 }}
      />
      <figcaption style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', justifyContent: justify }}>
        {block.authorAvatarUrl ? (
          <img src={block.authorAvatarUrl} alt={block.authorName} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <div style={{
            width: 40, height: 40, borderRadius: '50%', background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
          }}>{(block.authorName || '?').slice(0, 1).toUpperCase()}</div>
        )}
        <div style={{ textAlign: 'left' }}>
          <EditableText
            value={block.authorName || ''}
            onChange={(authorName) => onChange({ ...block, authorName })}
            placeholder="Author name"
            multiline={false}
            style={{ fontWeight: 700, fontSize: '0.9625rem', color: 'var(--pg-text)' }}
          />
          <EditableText
            value={block.authorRole || ''}
            onChange={(authorRole) => onChange({ ...block, authorRole })}
            placeholder="Role, Company"
            multiline={false}
            style={{ fontSize: '0.8125rem', color: 'var(--pg-text-secondary)' }}
          />
        </div>
        {block.companyLogoUrl && (
          <img src={block.companyLogoUrl} alt="" style={{ marginLeft: 'auto', height: 24, opacity: 0.75 }} />
        )}
      </figcaption>
    </figure>
  )
}

// ─── Pricing ───────────────────────────────────────────

export function PricingCanvas({ block, onChange }: { block: PricingBlock; onChange: Patcher<PricingBlock> }) {
  const updateTier = (idx: number, patch: Partial<typeof block.tiers[number]>) => {
    const tiers = [...block.tiers]; tiers[idx] = { ...tiers[idx], ...patch }
    onChange({ ...block, tiers })
  }
  const removeTier = (idx: number) => onChange({ ...block, tiers: block.tiers.filter((_, k) => k !== idx) })
  const addTier = () => onChange({ ...block, tiers: [...block.tiers, makePricingTier()] })

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <header style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <EditableText
          value={block.heading || ''}
          onChange={(heading) => onChange({ ...block, heading })}
          placeholder="Pricing heading"
          multiline={false}
          style={{ margin: 0, fontSize: '1.7625rem', fontWeight: 800, color: 'var(--pg-text)' }}
        />
        <EditableText
          value={block.subheading || ''}
          onChange={(subheading) => onChange({ ...block, subheading })}
          placeholder="Pricing subheading"
          style={{ margin: 0, fontSize: '1.0125rem', color: 'var(--pg-text-secondary)' }}
        />
      </header>
      <div style={{
        display: 'grid', gridTemplateColumns: responsiveGrid(block.columns, 240, 16),
        gap: 16, alignItems: 'stretch',
      }}>
        {block.tiers.map((t, i) => (
          <InlineListItem
            key={t.id}
            onRemove={block.tiers.length > 1 ? () => removeTier(i) : undefined}
            removeLabel="Remove tier"
            style={{ height: '100%' }}
          >
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 12, padding: 22, height: '100%',
              border: t.highlighted ? '2px solid var(--pg-primary)' : '1px solid var(--pg-border)',
              borderRadius: 12,
              background: t.highlighted ? 'var(--pg-surface-alt, var(--pg-surface))' : 'var(--pg-surface)',
              position: 'relative',
            }}>
              {t.badge !== undefined && (
                <EditableText
                  value={t.badge}
                  onChange={(badge) => updateTier(i, { badge })}
                  placeholder="Badge"
                  multiline={false}
                  style={{
                    alignSelf: 'center', padding: '3px 10px', fontSize: '0.8125rem', fontWeight: 700,
                    background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', borderRadius: 999,
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                  }}
                />
              )}
              <EditableText
                value={t.name || ''}
                onChange={(name) => updateTier(i, { name })}
                placeholder="Tier name"
                multiline={false}
                style={{ fontSize: '1.0125rem', fontWeight: 700, color: 'var(--pg-text)' }}
              />
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                <EditableText
                  value={t.price || ''}
                  onChange={(price) => updateTier(i, { price })}
                  placeholder="$0"
                  multiline={false}
                  style={{ fontSize: '2.2625rem', fontWeight: 800, color: 'var(--pg-text)' }}
                />
                <EditableText
                  value={t.period || ''}
                  onChange={(period) => updateTier(i, { period })}
                  placeholder="/mo"
                  multiline={false}
                  style={{ fontSize: '0.9125rem', color: 'var(--pg-text-secondary)' }}
                />
              </div>
              <EditableText
                value={t.description || ''}
                onChange={(description) => updateTier(i, { description })}
                placeholder="Description"
                style={{ margin: 0, fontSize: '0.8825rem', color: 'var(--pg-text-secondary)', lineHeight: 1.5 }}
              />
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                {t.features.map((f, fi) => (
                  <li key={fi} className="pg-list-item" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.9125rem', color: 'var(--pg-text)' }}>
                    <Check size={16} style={{ marginTop: 2, color: 'var(--pg-primary)', flexShrink: 0 }} />
                    <EditableText
                      value={f}
                      onChange={(text) => {
                        const features = [...t.features]; features[fi] = text
                        updateTier(i, { features })
                      }}
                      placeholder="Feature"
                      multiline={false}
                      style={{ flex: 1 }}
                    />
                    {t.features.length > 1 && (
                      <button
                        type="button"
                        className="pg-item-remove"
                        style={{ position: 'static', opacity: 0 }}
                        onClick={(e) => {
                          e.preventDefault(); e.stopPropagation()
                          const features = t.features.filter((_, k) => k !== fi)
                          updateTier(i, { features })
                        }}
                        aria-label="Remove feature"
                        title="Remove feature"
                      >×</button>
                    )}
                  </li>
                ))}
                <li>
                  <AddItemButton
                    label="Add feature"
                    onClick={() => updateTier(i, { features: [...t.features, 'New feature'] })}
                  />
                </li>
              </ul>
              {t.cta && <div style={{ marginTop: 'auto' }}><PageButton btn={t.cta} /></div>}
            </div>
          </InlineListItem>
        ))}
      </div>
      <div><AddItemButton label="Add tier" onClick={addTier} /></div>
    </section>
  )
}

// ─── FAQ ───────────────────────────────────────────────

export function FaqCanvas({ block, onChange }: { block: FaqBlock; onChange: Patcher<FaqBlock> }) {
  const [openId, setOpenId] = useState<string | null>(null)
  const updateItem = (idx: number, patch: Partial<typeof block.items[number]>) => {
    const items = [...block.items]; items[idx] = { ...items[idx], ...patch }
    onChange({ ...block, items })
  }
  const removeItem = (idx: number) => onChange({ ...block, items: block.items.filter((_, k) => k !== idx) })
  const addItem = () => onChange({
    ...block, items: [...block.items, { id: newId('faq'), question: '', answer: '' }],
  })
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <EditableText
        value={block.heading || ''}
        onChange={(heading) => onChange({ ...block, heading })}
        placeholder="FAQ heading"
        multiline={false}
        style={{ margin: 0, fontSize: '1.5625rem', fontWeight: 800, color: 'var(--pg-text)' }}
      />
      <div style={{
        border: '1px solid var(--pg-border)', borderRadius: 12, overflow: 'hidden',
        background: 'var(--pg-surface)',
      }}>
        {block.items.map((it, i) => {
          const open = openId === it.id
          return (
            <InlineListItem
              key={it.id}
              onRemove={block.items.length > 1 ? () => removeItem(i) : undefined}
              removeLabel="Remove FAQ"
              style={{ borderTop: i === 0 ? 'none' : '1px solid var(--pg-border)' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '14px 18px' }}>
                <EditableText
                  value={it.question || ''}
                  onChange={(question) => updateItem(i, { question })}
                  placeholder="Question"
                  multiline={false}
                  style={{ flex: 1, fontWeight: 600, fontSize: '1.0125rem', color: 'var(--pg-text)' }}
                />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setOpenId(open ? null : it.id) }}
                  style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', color: 'var(--pg-text-secondary)' }}
                  title={open ? 'Collapse' : 'Expand'}
                >
                  <ChevronDown size={18} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                </button>
              </div>
              {open && (
                <div style={{ padding: '0 18px 16px' }}>
                  <EditableText
                    value={it.answer || ''}
                    onChange={(answer) => updateItem(i, { answer })}
                    placeholder="Answer"
                    style={{ fontSize: '0.9425rem', color: 'var(--pg-text-secondary)', lineHeight: 1.6 }}
                  />
                </div>
              )}
            </InlineListItem>
          )
        })}
      </div>
      <div><AddItemButton label="Add Q&A" onClick={addItem} /></div>
    </section>
  )
}

// ─── Tabs ──────────────────────────────────────────────

export function TabsCanvas({ block, onChange }: { block: TabsBlock; onChange: Patcher<TabsBlock> }) {
  const [activeId, setActiveId] = useState<string>(block.items[0]?.id || '')
  const active = block.items.find((i) => i.id === activeId) || block.items[0]
  const variant = block.variant || 'underline'
  const overflow = block.overflow || 'wrap'
  const updateItem = (idx: number, patch: Partial<typeof block.items[number]>) => {
    const items = [...block.items]; items[idx] = { ...items[idx], ...patch }
    onChange({ ...block, items })
  }
  const removeItem = (idx: number) => {
    const next = block.items.filter((_, k) => k !== idx)
    onChange({ ...block, items: next })
    if (block.items[idx].id === activeId && next[0]) setActiveId(next[0].id)
  }
  const addItem = () => {
    const fresh = { id: newId('tab'), label: 'New tab', body: '', blocks: [] }
    onChange({ ...block, items: [...block.items, fresh] })
    setActiveId(fresh.id)
  }

  return (
    <div>
      <div style={{
        display: 'flex', gap: variant === 'pill' ? 4 : 0,
        flexWrap: overflow === 'wrap' ? 'wrap' : 'nowrap',
        overflowX: overflow === 'scroll' ? 'auto' : 'visible',
        borderBottom: variant === 'underline' ? '1px solid var(--pg-border)' : 'none',
        background: variant === 'pill' ? 'var(--pg-surface)' : 'transparent',
        padding: variant === 'pill' ? 4 : 0,
        borderRadius: variant === 'pill' ? 8 : 0,
        width: variant === 'pill' && overflow === 'wrap' ? 'fit-content' : '100%',
        maxWidth: '100%',
      }}>
        {block.items.map((it, i) => {
          const isActive = it.id === active?.id
          const canRemove = block.items.length > 1
          return (
            <div
              key={it.id}
              onClick={() => { if (!isActive) setActiveId(it.id) }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: variant === 'pill' ? '6px 8px 6px 14px' : '10px 8px 10px 14px',
                background: variant === 'pill' ? (isActive ? 'var(--pg-primary)' : 'transparent') : 'transparent',
                color: variant === 'pill' ? (isActive ? 'var(--pg-on-primary, #fff)' : 'var(--pg-muted, var(--pg-text-secondary))') : (isActive ? 'var(--pg-primary)' : 'var(--pg-muted, var(--pg-text-secondary))'),
                fontWeight: isActive ? 700 : 500,
                borderRadius: variant === 'pill' ? 6 : 0,
                borderBottom: variant === 'underline' ? (isActive ? '2px solid var(--pg-primary)' : '2px solid transparent') : 'none',
                cursor: 'pointer', fontSize: '0.9425rem', transition: 'all 0.15s',
                flexShrink: 0, whiteSpace: overflow === 'scroll' ? 'nowrap' : 'normal',
              }}
            >
              {isActive ? (
                <EditableText
                  value={it.label}
                  onChange={(label) => updateItem(i, { label })}
                  placeholder="Tab label"
                  multiline={false}
                />
              ) : (
                <span style={{ whiteSpace: 'pre-wrap' }}>{it.label || 'Tab label'}</span>
              )}
              {canRemove && (
                <button
                  type="button"
                  aria-label="Remove tab"
                  title="Remove tab"
                  tabIndex={-1}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeItem(i) }}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    border: 'none', borderRadius: 4, padding: 0, marginLeft: 2,
                    background: 'transparent',
                    color: variant === 'pill' && isActive ? 'rgba(255,255,255,0.85)' : 'var(--pg-text-secondary)',
                    opacity: 0.65, cursor: 'pointer', flexShrink: 0,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'rgba(127,127,127,0.18)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.65'; e.currentTarget.style.background = 'transparent' }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          )
        })}
        <AddItemButton label="Add tab" onClick={addItem} style={{ marginTop: 0, marginLeft: 8 }} />
      </div>
      {active && (() => {
        const activeIdx = block.items.findIndex(i => i.id === active.id)
        const hasChildren = !!active.blocks && active.blocks.length > 0
        return (
          <div style={{ padding: '16px 4px', fontSize: '0.9825rem', color: 'var(--pg-text)', lineHeight: 1.6 }}>
            {hasChildren ? (
              <ChildBlockList
                blocks={active.blocks!}
                onChange={(next) => updateItem(activeIdx, { blocks: next })}
              />
            ) : (
              <>
                <EditableText
                  value={active.body || ''}
                  onChange={(body) => updateItem(activeIdx, { body })}
                  placeholder="Tab content (plain text) — or insert a block below"
                />
                <div style={{ marginTop: 10 }}>
                  <ChildBlockList
                    blocks={[]}
                    onChange={(next) => updateItem(activeIdx, {
                      blocks: promoteLegacyText(active.body, next),
                      body: '',
                    })}
                  />
                </div>
              </>
            )}
          </div>
        )
      })()}
    </div>
  )
}

// ─── Accordion ─────────────────────────────────────────

export function AccordionCanvas({ block, onChange }: { block: AccordionBlock; onChange: Patcher<AccordionBlock> }) {
  const [openIds, setOpenIds] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    block.items.forEach((it) => { if (it.defaultOpen) init[it.id] = true })
    return init
  })
  const updateItem = (idx: number, patch: Partial<typeof block.items[number]>) => {
    const items = [...block.items]; items[idx] = { ...items[idx], ...patch }
    onChange({ ...block, items })
  }
  const removeItem = (idx: number) => onChange({ ...block, items: block.items.filter((_, k) => k !== idx) })
  const addItem = () => onChange({
    ...block, items: [...block.items, { id: newId('acc'), title: '', body: '', blocks: [] }],
  })
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {block.items.map((it, i) => {
        const open = openIds[it.id]
        const hasChildren = !!it.blocks && it.blocks.length > 0
        return (
          <InlineListItem
            key={it.id}
            onRemove={block.items.length > 1 ? () => removeItem(i) : undefined}
            removeLabel="Remove section"
            style={{ border: '1px solid var(--pg-border)', borderRadius: 8, background: 'var(--pg-surface)', overflow: 'hidden' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 8 }}>
              <EditableText
                value={it.title || ''}
                onChange={(title) => updateItem(i, { title })}
                placeholder="Section title"
                multiline={false}
                style={{ flex: 1, fontWeight: 600, fontSize: '0.9825rem', color: 'var(--pg-text)' }}
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpenIds(p => ({ ...p, [it.id]: !p[it.id] })) }}
                style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', color: 'var(--pg-text-secondary)' }}
                title={open ? 'Collapse' : 'Expand'}
              >
                <ChevronDown size={16} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
            </div>
            {open && (
              <div style={{ padding: '0 16px 14px' }}>
                {hasChildren ? (
                  <ChildBlockList
                    blocks={it.blocks!}
                    onChange={(next) => updateItem(i, { blocks: next })}
                  />
                ) : (
                  <>
                    <EditableText
                      value={it.body || ''}
                      onChange={(body) => updateItem(i, { body })}
                      placeholder="Section body (plain text) — or insert a block below"
                      style={{ fontSize: '0.9225rem', color: 'var(--pg-text-secondary)', lineHeight: 1.55 }}
                    />
                    <div style={{ marginTop: 10 }}>
                      <ChildBlockList
                        blocks={[]}
                        onChange={(next) => updateItem(i, {
                          blocks: promoteLegacyText(it.body, next),
                          body: '',
                        })}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </InlineListItem>
        )
      })}
      <div><AddItemButton label="Add section" onClick={addItem} /></div>
    </section>
  )
}

// ─── Callout ───────────────────────────────────────────

const CALLOUT_STYLES = {
  info:    { bg: 'var(--pg-info-bg, rgba(59,130,246,0.08))',    border: 'var(--pg-info, #3b82f6)',    color: 'var(--pg-info, #3b82f6)',    icon: 'ℹ' },
  success: { bg: 'var(--pg-success-bg, rgba(34,197,94,0.08))',  border: 'var(--pg-success, #22c55e)', color: 'var(--pg-success, #22c55e)', icon: '✓' },
  warning: { bg: 'var(--pg-warning-bg, rgba(234,179,8,0.10))',  border: 'var(--pg-warning, #d97706)', color: 'var(--pg-warning, #d97706)', icon: '⚠' },
  error:   { bg: 'var(--pg-error-bg, rgba(239,68,68,0.08))',    border: 'var(--pg-error, #ef4444)',   color: 'var(--pg-error, #ef4444)',   icon: '✕' },
  tip:     { bg: 'var(--pg-info-bg, rgba(0,189,165,0.08))',     border: 'var(--pg-accent)',           color: 'var(--pg-accent)',           icon: '💡' },
} as const

// Multi-char ASCII identifier ⇒ Material Icon name (e.g. "lightbulb"); a
// single character or anything containing non-identifier chars ⇒ raw glyph.
// Explicit width on the span keeps the icon column from expanding when
// the Material Icons font hasn't loaded yet (the literal ligature text
// would otherwise push the body to a one-word column on narrow widths).
const CALLOUT_ICON_PX_C: Record<NonNullable<CalloutBlock['iconSize']>, number> = {
  sm: 16, md: 20, lg: 28,
}

// Generators (LLMs in particular) emit Lucide-style hyphenated names
// like "alert-triangle". Map the common ones to their Material Icons
// equivalent so the editor + runtime show an icon instead of the raw
// text. Anything not in the table is normalised by replacing '-' with
// '_'; Material Icons matches a fair number directly that way
// (check_circle, arrow_right, …).
// `normalizeIconName` + the icon-name aliases live in
// `@twasta/page-runtime/iconMap` so both the designer canvas and the
// extracted renderer share one source. Import for local use; re-export
// for back-compat with any callers that still reach it via this file.
import { normalizeIconName } from './iconMap'
export { normalizeIconName }

function renderCalloutIcon(value: string, color: string, size: NonNullable<CalloutBlock['iconSize']>) {
  const px = CALLOUT_ICON_PX_C[size]
  const baseStyle: React.CSSProperties = {
    fontSize: px, width: px, height: px, lineHeight: 1, color,
    flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  }
  const normalized = normalizeIconName(value)
  if (/^[a-z0-9_]{2,}$/i.test(normalized)) {
    return <span className="material-icons" style={baseStyle}>{normalized}</span>
  }
  return <span style={baseStyle}>{normalized}</span>
}

export function CalloutCanvas({ block, onChange }: { block: CalloutBlock; onChange: Patcher<CalloutBlock> }) {
  const variant = CALLOUT_STYLES[block.variant]
  // Mirror the runtime defaults so the editor preview matches what the
  // user will see at runtime.
  const iconPosition = block.iconPosition || 'left'
  const iconSize = block.iconSize || 'md'
  const align = block.align || 'left'
  const padding = block.padding ?? 14
  const gap = block.gap ?? 12
  const iconNode = iconPosition !== 'none' && (
    <div style={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0 }}>
      {renderCalloutIcon(block.icon || variant.icon, variant.color, iconSize)}
    </div>
  )
  return (
    <aside style={{
      display: 'flex',
      flexDirection: iconPosition === 'top' ? 'column' : 'row',
      alignItems: iconPosition === 'top' && align === 'center' ? 'center' : 'flex-start',
      gap, padding,
      background: variant.bg, borderLeft: `3px solid ${variant.border}`, borderRadius: 8,
    }}>
      {iconNode}
      {/* min-width: 0 + word-break let the body shrink below long
          unbreakable tokens like {{var}}; without these, the column
          inflates and squeezes the icon side on narrow viewports. */}
      <div style={{ flex: 1, minWidth: 0, textAlign: align }}>
        <EditableText
          value={block.title || ''}
          onChange={(title) => onChange({ ...block, title })}
          placeholder="Title"
          multiline={false}
          style={{
            fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))',
            marginBottom: 4, fontSize: '0.9825rem',
            wordBreak: 'break-word', overflowWrap: 'anywhere',
          }}
        />
        <EditableText
          value={block.body || ''}
          onChange={(body) => onChange({ ...block, body })}
          placeholder="Callout body"
          style={{
            fontSize: '0.9425rem', color: 'var(--pg-text)', lineHeight: 1.55,
            wordBreak: 'break-word', overflowWrap: 'anywhere',
          }}
        />
      </div>
    </aside>
  )
}

// ─── Newsletter ────────────────────────────────────────

export function NewsletterCanvas({ block, onChange }: { block: NewsletterBlock; onChange: Patcher<NewsletterBlock> }) {
  const align = block.align || 'center'
  const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'
  return (
    <section style={{
      ...backgroundStyle(block.background), padding: 28, borderRadius: 12,
      display: 'flex', flexDirection: 'column', gap: 12,
      alignItems: justify, textAlign: align,
    }}>
      <EditableText
        value={block.heading || ''}
        onChange={(heading) => onChange({ ...block, heading })}
        placeholder="Subscribe to our newsletter"
        multiline={false}
        style={{ margin: 0, fontSize: '1.4625rem', fontWeight: 800, color: 'var(--pg-text)' }}
      />
      <EditableText
        value={block.subheading || ''}
        onChange={(subheading) => onChange({ ...block, subheading })}
        placeholder="Get the latest updates in your inbox."
        style={{ margin: 0, fontSize: '0.9625rem', color: 'var(--pg-text-secondary)', maxWidth: 520 }}
      />
      <form
        onSubmit={(e) => e.preventDefault()}
        style={{ display: 'flex', gap: 8, width: '100%', maxWidth: 480, flexWrap: 'wrap' }}
      >
        <input
          type="email" disabled
          placeholder={block.placeholder || 'you@example.com'}
          style={{
            flex: 1, minWidth: 200, padding: '10px 14px', fontSize: '0.9625rem',
            border: '1px solid var(--pg-border)', borderRadius: 8,
            background: 'var(--pg-surface-muted, var(--pg-bg))', color: 'var(--pg-text)', outline: 'none',
          }}
        />
        <div style={{
          padding: '10px 18px', fontSize: '0.9625rem', fontWeight: 600,
          background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', borderRadius: 8,
        }}>
          <EditableText
            value={block.buttonLabel || 'Subscribe'}
            onChange={(buttonLabel) => onChange({ ...block, buttonLabel })}
            placeholder="Subscribe"
            multiline={false}
          />
        </div>
      </form>
    </section>
  )
}

// ─── Contact form ──────────────────────────────────────

export function ContactFormCanvas({ block, onChange }: { block: ContactFormBlock; onChange: Patcher<ContactFormBlock> }) {
  const align = block.align || 'left'
  const updateField = (idx: number, patch: Partial<typeof block.fields[number]>) => {
    const fields = [...block.fields]; fields[idx] = { ...fields[idx], ...patch }
    onChange({ ...block, fields })
  }
  const removeField = (idx: number) => onChange({ ...block, fields: block.fields.filter((_, k) => k !== idx) })
  const addField = () => onChange({
    ...block, fields: [...block.fields, { id: newId('fld'), name: `field_${block.fields.length + 1}`, label: 'Field', type: 'text' }],
  })
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <header style={{ textAlign: align, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <EditableText
          value={block.heading || ''}
          onChange={(heading) => onChange({ ...block, heading })}
          placeholder="Heading"
          multiline={false}
          style={{ margin: 0, fontSize: '1.4625rem', fontWeight: 700, color: 'var(--pg-text)' }}
        />
        <EditableText
          value={block.subheading || ''}
          onChange={(subheading) => onChange({ ...block, subheading })}
          placeholder="Subheading"
          style={{ margin: 0, fontSize: '0.9125rem', color: 'var(--pg-text-secondary)' }}
        />
      </header>
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 12,
        padding: 20, background: 'var(--pg-surface)',
        border: '1px solid var(--pg-border)', borderRadius: 12,
      }}>
        {block.fields.map((f, i) => (
          <InlineListItem
            key={f.id}
            onRemove={block.fields.length > 1 ? () => removeField(i) : undefined}
            removeLabel="Remove field"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <EditableText
                value={f.label || ''}
                onChange={(label) => updateField(i, { label })}
                placeholder="Field label"
                multiline={false}
                style={{ fontSize: '0.8425rem', fontWeight: 600, color: 'var(--pg-text-secondary)' }}
              />
              {f.type === 'textarea' ? (
                <textarea
                  rows={3} disabled
                  placeholder={f.placeholder}
                  style={{
                    padding: '8px 12px', fontSize: '0.9425rem',
                    border: '1px solid var(--pg-border)', borderRadius: 6,
                    background: 'var(--pg-bg)', color: 'var(--pg-text)',
                    resize: 'vertical', fontFamily: 'inherit',
                  }}
                />
              ) : (
                <input
                  type={f.type as string} disabled
                  placeholder={f.placeholder}
                  style={{
                    padding: '8px 12px', fontSize: '0.9425rem',
                    border: '1px solid var(--pg-border)', borderRadius: 6,
                    background: 'var(--pg-bg)', color: 'var(--pg-text)',
                  }}
                />
              )}
            </div>
          </InlineListItem>
        ))}
        <AddItemButton label="Add field" onClick={addField} />
        <div style={{
          alignSelf: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
          padding: '10px 20px', fontSize: '0.9625rem', fontWeight: 600,
          background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', borderRadius: 8,
        }}>
          <EditableText
            value={block.buttonLabel || 'Send message'}
            onChange={(buttonLabel) => onChange({ ...block, buttonLabel })}
            placeholder="Submit"
            multiline={false}
          />
        </div>
      </div>
    </section>
  )
}

// ─── Gallery ───────────────────────────────────────────

export function GalleryCanvas({ block, onChange }: { block: GalleryBlock; onChange: Patcher<GalleryBlock> }) {
  const updateItem = (idx: number, patch: Partial<typeof block.items[number]>) => {
    const items = [...block.items]; items[idx] = { ...items[idx], ...patch }
    onChange({ ...block, items })
  }
  const removeItem = (idx: number) => onChange({ ...block, items: block.items.filter((_, k) => k !== idx) })
  const addItem = () => onChange({ ...block, items: [...block.items, makeGalleryItem()] })
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{
        display: 'grid', gridTemplateColumns: responsiveGrid(block.columns, 160, block.gap ?? 12),
        gap: block.gap ?? 12,
      }}>
        {block.items.map((it, i) => (
          <InlineListItem
            key={it.id}
            onRemove={block.items.length > 1 ? () => removeItem(i) : undefined}
            removeLabel="Remove image"
          >
            <figure style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {it.url ? (
                <img
                  src={it.url} alt={it.caption || ''}
                  style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: block.rounded ? 8 : 0, display: 'block' }}
                />
              ) : (
                <div style={{
                  width: '100%', height: 160, background: 'var(--pg-bg)',
                  border: '1px dashed var(--pg-border)', borderRadius: block.rounded ? 8 : 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--pg-text-secondary)', fontSize: '0.8625rem',
                }}>Image URL…</div>
              )}
              <EditableText
                value={it.caption || ''}
                onChange={(caption) => updateItem(i, { caption })}
                placeholder="Caption"
                multiline={false}
                style={{ marginTop: 4, fontSize: '0.8125rem', color: 'var(--pg-text-secondary)', textAlign: 'center' }}
              />
            </figure>
          </InlineListItem>
        ))}
      </div>
      <div><AddItemButton label="Add image" onClick={addItem} /></div>
    </div>
  )
}

// ─── Logo cloud ────────────────────────────────────────

export function LogoCloudCanvas({ block, onChange }: { block: LogoCloudBlock; onChange: Patcher<LogoCloudBlock> }) {
  const updateItem = (idx: number, patch: Partial<typeof block.items[number]>) => {
    const items = [...block.items]; items[idx] = { ...items[idx], ...patch }
    onChange({ ...block, items })
  }
  const removeItem = (idx: number) => onChange({ ...block, items: block.items.filter((_, k) => k !== idx) })
  const addItem = () => onChange({ ...block, items: [...block.items, makeLogoItem()] })
  return (
    <section style={{
      ...backgroundStyle(block.background),
      borderRadius: bgHasFill(block.background) ? 12 : 0,
      padding: bgHasFill(block.background) ? '24px' : '8px 0',
      display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center',
    }}>
      <EditableText
        value={block.title || ''}
        onChange={(title) => onChange({ ...block, title })}
        placeholder="Trusted by teams everywhere"
        multiline={false}
        style={{ margin: 0, fontSize: '0.8425rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--pg-text-secondary)' }}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'center' }}>
        {block.items.map((lg, i) => (
          <InlineListItem
            key={lg.id}
            onRemove={block.items.length > 1 ? () => removeItem(i) : undefined}
            removeLabel="Remove logo"
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              {lg.url ? (
                <img
                  src={lg.url} alt={lg.name || ''}
                  style={{ height: 32, opacity: 0.8, filter: block.monochrome ? 'grayscale(100%)' : 'none' }}
                />
              ) : (
                <div style={{
                  padding: '6px 14px', borderRadius: 6,
                  background: 'var(--pg-surface)', border: '1px dashed var(--pg-border)',
                  fontSize: '0.9125rem', fontWeight: 600, color: 'var(--pg-text-secondary)',
                }}>{lg.name || 'Logo'}</div>
              )}
              <EditableText
                value={lg.name || ''}
                onChange={(name) => updateItem(i, { name })}
                placeholder="Brand"
                multiline={false}
                style={{ fontSize: '0.8125rem', color: 'var(--pg-text-secondary)' }}
              />
            </div>
          </InlineListItem>
        ))}
      </div>
      <div><AddItemButton label="Add logo" onClick={addItem} /></div>
    </section>
  )
}

// ─── Buttons ───────────────────────────────────────────

/**
 * What an Add-to-cart button asks the visitor before adding — a quantity
 * stepper, a date calendar — drawn as a still preview around the editable
 * button. The live ones are the shared cart script's (page-runtime cart.ts);
 * this only shows the author that they are switched on and where they sit.
 */
function CartChooserCanvas({ spec, children }: { spec: ButtonSpec; children: React.ReactNode }) {
  const click = spec.on_click
  if (!click || click.kind !== 'add_to_cart' || (!click.qtyPicker && !click.booking)) return <>{children}</>
  const b = click.booking
  const perDay = b?.unit === 'day'
  const muted: React.CSSProperties = { fontSize: '0.8425rem', color: 'var(--pg-muted, var(--pg-text-secondary))' }
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 8 }}>
      {b && (
        <div style={{
          width: 260, border: '1px dashed var(--pg-border)', borderRadius: 10, padding: 10,
          background: 'var(--pg-surface)', display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <strong style={{ fontSize: '0.9125rem', color: 'var(--pg-heading, var(--pg-text))' }}>
            📅 {b.startLabel || (perDay ? 'Pick-up' : 'Check-in')} → {b.endLabel || (perDay ? 'Return' : 'Check-out')}
          </strong>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {Array.from({ length: 14 }, (_, i) => (
              <span key={i} style={{
                height: 16, borderRadius: 4,
                background: i === 3 || i === 6 ? 'var(--pg-primary)' : i > 3 && i < 6 ? 'color-mix(in srgb, var(--pg-primary) 16%, transparent)' : 'var(--pg-surface-alt, rgba(0,0,0,0.04))',
              }} />
            ))}
          </div>
          <span style={muted}>
            Calendar · priced per {perDay ? 'day' : 'night'}
            {b.minUnits ? ` · min ${b.minUnits}` : ''}{b.maxUnits ? ` · max ${b.maxUnits}` : ''}
          </span>
        </div>
      )}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {click.qtyPicker && (
          <>
            {click.qtyLabel && <span style={muted}>{click.qtyLabel}</span>}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 12, padding: '6px 12px',
              border: '1px solid var(--pg-border)', borderRadius: 999, fontWeight: 700,
              color: 'var(--pg-text)', background: 'var(--pg-surface)',
            }}>
              <span>−</span><span>{Math.max(1, click.qty || 1)}</span><span>+</span>
            </span>
          </>
        )}
        {children}
      </div>
    </div>
  )
}

export function ButtonCanvas({ block, onChange }: { block: ButtonBlock; onChange: Patcher<ButtonBlock> }) {
  const justify = block.align === 'center' ? 'center' : block.align === 'right' ? 'flex-end' : 'flex-start'
  // Variants reference theme tokens so they stay readable on every preset.
  const VARIANT: Record<ButtonSpec['variant'], React.CSSProperties> = {
    primary:   { background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', border: '1px solid var(--pg-primary)' },
    secondary: { background: 'var(--pg-surface)', color: 'var(--pg-on-surface, var(--pg-text))', border: '1px solid var(--pg-border)' },
    outline:   { background: 'transparent', color: 'currentColor', border: '1.5px solid currentColor' },
    ghost:     { background: 'transparent', color: 'currentColor', border: '1px solid transparent' },
    link:      { background: 'transparent', color: 'var(--pg-link, var(--pg-primary))', border: 'none', textDecoration: 'underline' },
  }
  const SIZE: Record<NonNullable<ButtonSpec['size']>, React.CSSProperties> = {
    sm: { padding: '6px 12px',  fontSize: '0.8625rem'  },
    md: { padding: '9px 18px',  fontSize: '0.9625rem'  },
    lg: { padding: '12px 24px', fontSize: '1.0625rem'    },
  }
  // Same coercion the runtime renderer applies, so a flat-authored button
  // block (label/variant on the block itself) is editable here instead of
  // crashing the canvas.
  const btn = buttonSpecOf(block)
  return (
    <div style={{ display: 'flex', justifyContent: justify }}>
      <CartChooserCanvas spec={btn}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 8, fontWeight: 600,
          ...VARIANT[btn.variant], ...SIZE[btn.size || 'md'],
        }}>
          {btn.icon && <PageIcon name={btn.icon} size={16} fallback={<span>{btn.icon}</span>} />}
          <EditableText
            value={btn.label || ''}
            // Spread the stored spec too: buttonSpecOf rebuilds from a fixed
            // whitelist, so editing the label must not drop any other key.
            onChange={(label) => onChange({ ...block, button: { ...(block.button || {}), ...btn, label } })}
            placeholder="Button label"
            multiline={false}
          />
        </div>
      </CartChooserCanvas>
    </div>
  )
}

export function ButtonGroupCanvas({ block, onChange }: { block: ButtonGroupBlock; onChange: Patcher<ButtonGroupBlock> }) {
  const justify = block.align === 'center' ? 'center' : block.align === 'right' ? 'flex-end' : 'flex-start'
  // Variants reference theme tokens so they stay readable on every preset.
  const VARIANT: Record<ButtonSpec['variant'], React.CSSProperties> = {
    primary:   { background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', border: '1px solid var(--pg-primary)' },
    secondary: { background: 'var(--pg-surface)', color: 'var(--pg-on-surface, var(--pg-text))', border: '1px solid var(--pg-border)' },
    outline:   { background: 'transparent', color: 'currentColor', border: '1.5px solid currentColor' },
    ghost:     { background: 'transparent', color: 'currentColor', border: '1px solid transparent' },
    link:      { background: 'transparent', color: 'var(--pg-link, var(--pg-primary))', border: 'none', textDecoration: 'underline' },
  }
  const SIZE: Record<NonNullable<ButtonSpec['size']>, React.CSSProperties> = {
    sm: { padding: '6px 12px',  fontSize: '0.8625rem'  },
    md: { padding: '9px 18px',  fontSize: '0.9625rem'  },
    lg: { padding: '12px 24px', fontSize: '1.0625rem'    },
  }
  const buttons = (block.buttons || []).filter(Boolean)
  return (
    <div style={{ display: 'flex', gap: block.gap ?? 8, justifyContent: justify, flexWrap: 'wrap' }}>
      {buttons.map((b, i) => (
        <InlineListItem
          key={b.id || i}
          onRemove={buttons.length > 1 ? () => onChange({ ...block, buttons: buttons.filter((_, k) => k !== i), bindings: reindexListBindings(block, 'buttons', removeOrder(buttons.length, i)) }) : undefined}
          removeLabel="Remove button"
          style={{ display: 'inline-flex' }}
        >
          <CartChooserCanvas spec={b}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 8, fontWeight: 600,
              ...VARIANT[b.variant], ...SIZE[b.size || 'md'],
            }}>
              {b.icon && <PageIcon name={b.icon} size={16} fallback={<span>{b.icon}</span>} />}
              <EditableText
                value={b.label || ''}
                onChange={(label) => {
                  const next = [...buttons]; next[i] = { ...b, label }
                  onChange({ ...block, buttons: next })
                }}
                placeholder="Button"
                multiline={false}
              />
            </div>
          </CartChooserCanvas>
        </InlineListItem>
      ))}
      <AddItemButton
        label="Add button"
        onClick={() => onChange({
          ...block,
          buttons: [...buttons, { id: newId('btn'), label: 'Button', url: '#', variant: 'outline', size: 'md' }],
        })}
        style={{ marginTop: 0 }}
      />
    </div>
  )
}

// ─── Table ─────────────────────────────────────────────

export function TableCanvas({ block, onChange }: { block: TableBlock; onChange: Patcher<TableBlock> }) {
  const align = block.align ?? 'left'
  const cols = block.columns ?? []
  // Same normalisation the panel applies: an object-shaped row (keyed by
  // column id/label, as some agent-authored tables are) used to crash every
  // `r.map` below. Any edit writes the table back in the array shape.
  const rows = (block.rows ?? []).map((r) => toArrayRow(r, cols))

  const updateCell = (ri: number, ci: number, value: string) => {
    const next = rows.map((r, i) => i === ri ? r.map((c, j) => j === ci ? value : c) : [...r])
    onChange({ ...block, rows: next })
  }
  const updateColLabel = (ci: number, label: string) => {
    const next = cols.map((c, i) => i === ci ? { ...c, label } : c)
    onChange({ ...block, columns: next })
  }
  const addRow = () => {
    const fresh = cols.map(() => '')
    onChange({ ...block, rows: [...rows, fresh] })
  }
  const insertRowAt = (idx: number) => {
    const fresh = cols.map(() => '')
    onChange({ ...block, rows: [...rows.slice(0, idx), fresh, ...rows.slice(idx)] })
  }
  const removeRow = (ri: number) => onChange({ ...block, rows: rows.filter((_, i) => i !== ri) })
  const addCol = () => {
    const fresh = { id: newId('col'), label: 'New column', align: 'left' as const }
    const nextCols = [...cols, fresh]
    const nextRows = rows.map(r => [...r, ''])
    onChange({ ...block, columns: nextCols, rows: nextRows })
  }
  const removeCol = (ci: number) => {
    const nextCols = cols.filter((_, i) => i !== ci)
    const nextRows = rows.map(r => r.filter((_, i) => i !== ci))
    onChange({ ...block, columns: nextCols, rows: nextRows })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'stretch' }}>
      <EditableText
        value={block.heading || ''}
        onChange={(heading) => onChange({ ...block, heading })}
        placeholder="Table heading"
        multiline={false}
        style={{ fontSize: 17, fontWeight: 700, color: 'var(--pg-text)' }}
      />
      <EditableText
        value={block.subheading || ''}
        onChange={(subheading) => onChange({ ...block, subheading })}
        placeholder="Subheading"
        style={{ fontSize: 14, color: 'var(--pg-text-secondary)' }}
      />
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: block.compact ? 12 : 14 }}>
          <thead>
            <tr>
              {cols.map((c, ci) => (
                <th
                  key={c.id}
                  className="pg-list-item"
                  style={{
                    textAlign: c.align ?? 'left', padding: block.compact ? '6px 8px' : '10px 12px',
                    width: c.width, background: 'var(--pg-surface)',
                    border: block.bordered ? '1px solid var(--pg-border)' : 'none',
                    borderBottom: '1px solid var(--pg-border)',
                    fontWeight: 600, position: 'relative',
                  }}
                >
                  <EditableText
                    value={c.label || ''}
                    onChange={(label) => updateColLabel(ci, label)}
                    placeholder="Column"
                    multiline={false}
                  />
                  {cols.length > 1 && (
                    <button
                      type="button"
                      className="pg-item-remove"
                      style={{ top: 2, right: 2 }}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeCol(ci) }}
                      aria-label="Remove column"
                      title="Remove column"
                    >×</button>
                  )}
                </th>
              ))}
              <th style={{ padding: '4px 8px', background: 'var(--pg-surface)' }}>
                <button
                  type="button" className="pg-add-item" style={{ marginTop: 0, padding: '2px 8px' }}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); addCol() }}
                  title="Add column"
                >+</button>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className="pg-list-item" style={{ background: block.striped && ri % 2 === 1 ? 'var(--pg-surface)' : 'transparent', position: 'relative' }}>
                {cols.map((c, ci) => (
                  <td
                    key={c.id}
                    style={{
                      textAlign: c.align ?? 'left',
                      padding: block.compact ? '6px 8px' : '10px 12px',
                      border: block.bordered ? '1px solid var(--pg-border)' : 'none',
                      borderBottom: '1px solid var(--pg-border)',
                      verticalAlign: 'top',
                    }}
                  >
                    <EditableText
                      value={row[ci] ?? ''}
                      onChange={(value) => updateCell(ri, ci, value)}
                      placeholder="Cell"
                    />
                  </td>
                ))}
                <td style={{ padding: '0 4px', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    className="pg-add-item"
                    style={{ marginTop: 0, padding: '2px 6px', marginRight: 4 }}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); insertRowAt(ri + 1) }}
                    aria-label="Insert row below"
                    title="Insert row below"
                  >+</button>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      className="pg-item-remove"
                      style={{ position: 'static', opacity: 0.7 }}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeRow(ri) }}
                      aria-label="Remove row"
                      title="Remove row"
                    >×</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div><AddItemButton label="Add row" onClick={addRow} /></div>
    </div>
  )
}

// ─── eCommerce canvas previews ──────────────────────
//
// At the canvas level these blocks render as polished placeholder cards
// — the live UX (cascading selectors, catalogue modal, etc.) only makes
// sense at runtime against the ERP data. The author edits behaviour via
// the property panel.

interface EcomPlaceholderProps {
  icon: string
  title: string
  body: string
  bindings: { label: string; value: string | undefined }[]
  accent?: string
}

function EcomPlaceholder({ icon, title, body, bindings, accent }: EcomPlaceholderProps) {
  const accentColor = accent || 'var(--color-accent, #4c8dff)'
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 10, padding: 14,
      borderRadius: 10,
      border: '1px dashed var(--color-border, rgba(127,127,127,0.4))',
      background: 'var(--pg-surface-alt, rgba(76,141,255,0.04))',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          fontSize: 19, lineHeight: 1, padding: '6px 8px', borderRadius: 8,
          background: accentColor, color: '#fff',
        }}>{icon}</span>
        <span style={{ fontWeight: 600, fontSize: '1.0125rem' }}>{title}</span>
        <span style={{
          marginLeft: 'auto', fontSize: 13, fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.06em',
          color: 'var(--color-text-secondary)',
          padding: '2px 6px', border: '1px solid var(--color-border)', borderRadius: 999,
        }}>eCommerce</span>
      </div>
      <p style={{ margin: 0, fontSize: '0.9125rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
        {body}
      </p>
      {bindings.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {bindings.map((b, i) => (
            <span key={i} style={{
              fontSize: 13, padding: '3px 8px', borderRadius: 999,
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text)',
            }}>
              <span style={{ color: 'var(--color-text-secondary)' }}>{b.label}: </span>
              <code style={{ fontFamily: 'inherit' }}>{b.value || '—'}</code>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function sourceLabel(source: { kind: 'api'; endpointId: string; customUrl?: string } | { kind: 'transaction'; obj_name: string } | undefined): string {
  if (!source) return '—'
  if (source.kind === 'transaction') return `Transaction · ${source.obj_name || '(unset)'}`
  if (source.endpointId === 'custom') return `Custom · ${source.customUrl || '(no URL)'}`
  const def = ECOM_ENDPOINTS[source.endpointId as keyof typeof ECOM_ENDPOINTS]
  return def ? def.label : source.endpointId
}

function outputsLabel(outputs: { target: string; source: { source: string; name?: string } }[] | undefined): string {
  if (!outputs || outputs.length === 0) return '—'
  return outputs
    .filter((o) => o.source.source === 'page_input' && o.source.name)
    .map((o) => `${o.target}→${o.source.name}`)
    .join(', ') || '—'
}

export function EntityPickerCanvas({ block }: { block: EntityPickerBlock; onChange?: unknown }) {
  return (
    <EcomPlaceholder
      icon="🔎"
      title="Entity picker"
      body="Search & select an entity. Source can be a built-in API endpoint (customers/suppliers/etc.) or a transaction object. Selection writes to bound page variables, just like row selection in an embedded transaction list."
      bindings={[
        { label: 'source', value: sourceLabel(block.source) },
        { label: 'outputs', value: outputsLabel(block.outputBindings) },
        { label: 'toggle', value: outputVarName(block.toggleBinding) || undefined },
      ]}
    />
  )
}

export function NewCustomerFormCanvas({ block }: { block: NewCustomerFormBlock; onChange?: unknown }) {
  const url = resolveEndpointUrl(block.source)
  return (
    <EcomPlaceholder
      icon="👤"
      title="New customer form"
      body="Schema-driven customer-creation form. For the built-in customers endpoint the schema is fetched from ERP; for custom URLs the form falls back to a raw JSON body editor. On submit, writes the created record to the bound output vars."
      bindings={[
        { label: 'endpoint', value: url || '—' },
        { label: 'outputs', value: outputsLabel(block.outputBindings) },
        { label: 'toggle', value: outputVarName(block.toggleBinding) || undefined },
      ]}
    />
  )
}

export function ProductSelectionCanvas({ block }: { block: ProductSelectionBlock; onChange?: unknown }) {
  const mode = block.selectorMode || (block.catalogueMode === 'modal-only' ? 'catalogue' : block.catalogueMode === 'both' ? 'both' : 'wizard')
  return (
    <EcomPlaceholder
      icon="🛍"
      title="Product selection"
      body={`Cascading product → style → preferences → measurements → add to cart. ${mode === 'wizard' ? 'Inline wizard only.' : mode === 'catalogue' ? 'Full-screen catalogue only.' : 'Inline plus full-screen catalogue overlay.'}`}
      bindings={[
        { label: 'source', value: sourceLabel(block.source) },
        { label: 'cart', value: outputVarName(block.cartBinding) || '—' },
        { label: 'gate', value: outputVarName(block.gateBinding) || undefined },
        { label: 'mode', value: mode },
      ]}
    />
  )
}

export function ProductPreviewCanvas({
  block,
  onChange,
}: {
  block: ProductPreviewBlock
  // BlockNode passes the patcher for blocks that mutate their own
  // configuration. We use it so the inline placement editor can persist
  // the picked masterId on the block.
  onChange?: (b: ProductPreviewBlock) => void
}) {
  const masterId = block.placementMasterId || ''
  return (
    <ProductPreviewPlacementEditor
      masterId={masterId}
      onMasterIdChange={onChange ? (next) => onChange({ ...block, placementMasterId: next || undefined }) : undefined}
    />
  )
}

export function OrderSummaryCanvas({ block }: { block: OrderSummaryBlock; onChange?: unknown }) {
  return (
    <EcomPlaceholder
      icon="🛒"
      title="Order summary"
      body="Cart of captured items with one-line customisation summary, qty stepper, line totals, and subtotal. Edit re-opens the item in product_selection."
      bindings={[
        { label: 'cart', value: outputVarName(block.cartBinding) || '—' },
        { label: 'editable', value: block.editable === false ? 'false' : 'true' },
      ]}
    />
  )
}

export function OrderSubmitCanvas({ block }: { block: OrderSubmitBlock; onChange?: unknown }) {
  const url = resolveEndpointUrl(block.source)
  return (
    <EcomPlaceholder
      icon="✓"
      title="Order submit"
      body="Place-order action. POSTs to the configured endpoint (default /proteus-vision-ecom-ext/sales-orders), writes the response into bound output vars, and persists the success result on the document via captureState."
      bindings={[
        { label: 'endpoint', value: url || '—' },
        { label: 'customer', value: outputVarName(block.customerBinding) || '—' },
        { label: 'cart', value: outputVarName(block.cartBinding) || '—' },
        { label: 'outputs', value: outputsLabel(block.outputBindings) },
      ]}
    />
  )
}

export function PaymentSettingsCanvas(
  { block }: { block: PaymentSettingsBlock; onChange?: unknown },
) {
  // No live form on the canvas: it would ask the author to connect a gateway
  // while they are designing a page, and the endpoints behind it answer only
  // to an administrator of the APPLICATION. What matters here is that the
  // author knows what they have placed and who will be able to use it.
  return (
    <EcomPlaceholder
      icon="\u{1F510}"
      title={block.heading || 'Payment settings'}
      body={'Lets this application\u2019s own administrator connect the gateway '
            + 'it charges on \u2014 the same form as Advanced \u2192 Payments, inside '
            + 'the app. Credentials are written straight to the server and '
            + 'encrypted there; nothing is ever sent back to the page.'}
      bindings={[
        { label: 'opens on', value: block.defaultProvider || 'razorpay' },
        { label: 'who may use it', value: 'the project owner, or FUNC-ADMIN' },
      ]}
    />
  )
}

export function PaymentCheckoutCanvas(
  { block }: { block: PaymentCheckoutBlock; onChange?: unknown },
) {
  // The canvas shows the BINDINGS rather than a fake pay button, because what
  // an author gets wrong here is which column holds the amount and who is
  // allowed to pay — not the look of a button.
  return (
    <EcomPlaceholder
      icon="💳"
      title="Payment checkout"
      body={'Takes payment for one saved record through this project\u2019s own '
            + 'gateway (Advanced \u2192 Payments). The amount is read from the '
            + 'record on the server \u2014 never sent by the browser.'}
      bindings={[
        { label: 'object', value: block.objName || '\u2014 choose one' },
        { label: 'record id var', value: block.txnIdVar ? `{{${block.txnIdVar}}}` : '\u2014' },
        { label: 'amount column', value: block.amountColumn || '\u2014 required' },
        {
          label: 'who may pay',
          value: block.ownerColumn
            ? `matches ${block.ownerColumn}`
            : 'signed-in app users only',
        },
        {
          label: 'marks paid',
          value: block.writeback?.statusColumn
            ? `${block.writeback.statusColumn} = ${block.writeback.statusValue || 'PAID'}`
            : 'nothing stamped',
        },
      ]}
    />
  )
}

function describeRef(ref: FilterValueRef | undefined): string {
  if (!ref) return '—'
  if (ref.source === 'page_input') return ref.name ? `{{${ref.name}}}` : '(page input)'
  return ref.value || '(empty)'
}

export function FileUploadCanvas({ block }: { block: FileUploadBlock; onChange?: unknown }) {
  const isImage = block.kind === 'image'
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 10, padding: 14, borderRadius: 10,
      border: '1px dashed var(--color-border, rgba(127,127,127,0.4))',
      background: 'var(--pg-surface-alt, rgba(76,141,255,0.04))',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          fontSize: 19, lineHeight: 1, padding: '6px 8px', borderRadius: 8,
          background: 'var(--color-accent, #4c8dff)', color: '#fff',
        }}>{isImage ? '🖼️' : '📎'}</span>
        <span style={{ fontWeight: 600, fontSize: '1.0125rem' }}>
          {block.label || (isImage ? 'Image upload' : 'File upload')}
        </span>
      </div>
      <p style={{ margin: 0, fontSize: '0.9125rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
        Visitors attach {isImage ? 'an image' : 'a file'}; it is stored under
        {' '}<code style={{ fontFamily: 'inherit' }}>{'<doc type>/<transaction id>/'}</code>{' '}
        and the URL is written to the bound page variable.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {[
          { label: 'doc type', value: describeRef(block.docType) },
          { label: 'txn id', value: describeRef(block.transactionId) },
          { label: 'stores in', value: block.valueVar ? `{{${block.valueVar}}}` : '—' },
          { label: 'preview', value: block.previewMode || 'auto' },
          { label: 'on upload', value: block.onUploadAction || '—' },
        ].map((b, i) => (
          <span key={i} style={{
            fontSize: 13, padding: '3px 8px', borderRadius: 999,
            background: 'var(--color-bg)', border: '1px solid var(--color-border)',
            color: 'var(--color-text)',
          }}>
            <span style={{ color: 'var(--color-text-secondary)' }}>{b.label}: </span>
            <code style={{ fontFamily: 'inherit' }}>{b.value}</code>
          </span>
        ))}
      </div>
    </div>
  )
}
